import {Component, DestroyRef, EventEmitter, inject, Input, OnDestroy, OnInit, Output} from "@angular/core";
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {NgbActiveModal} from "@ng-bootstrap/ng-bootstrap";
import {IColumnsDisplayLocal, IDisplayColumn} from '@shared/models/idb';
import {ExcelService} from '@shared/services/global';
import {AppService} from '@core/services/app.service';
import {UserService} from '@core/services/user.service';
import {AuthService} from "@core/authentication/auth.service";
import {FormatPercentPipe} from '@shared/pipes/global';


type ISettingColumnsExcel = {
  ref: string,
  name: string,
  display?: boolean
}

type listPageName = 'recordAll' | 'recordPage' | 'recordGroupPsParent' | 'recordSelected';

type IResSetting = {
  excelListPage: listPageName,
  column?: string[],
}

type IStates = {
  columnExcelOption: 'column' | 'all' | string, // Tất cả | tự chọn cột | fbId
  colsAll: boolean, // Chon nhanh tất cả cột | hủy tất cả cột
  recordAllFullPage: boolean,
  excelPageChecked: listPageName,
  settingColumns: IDisplayColumn[],
  isSubmit: boolean,
  rembExcelSetting: boolean,
}
export type IExcelSettingOutput = {
  // Dữ liệu sẽ gửi kèm lên server
  params: {
    format: 'jsExcel',
    // Chọn cột:
    // '': không xử lý gì khác => tự chọn cột
    // 'full': Lặp lại thông tin đơn hàng ở từng dòng sản phẩm nếu đơn hàng hoặc hóa đơn có từ 2 sản phẩm trở lên.
    // 'fbPsid': Đơn hàng xuất fbPsid
    typeExcel: 'full' | 'fbPsid' | '',

    // Xuất dữ liệu gộp "theo sản phẩm cha"
    // Default = false
    exportGroupToPsParent?: boolean,
  }

  // Chỉ xuất dữ liệu trang hiện tại
  // Default = all
  // + all: tất cả các trang
  // + current: trang hiện tại
  // + records: các bản ghi đã chọn
  exportType: 'all' | 'current' | 'records',

  // Chỉ khi có truyền columns ở input thì mới có dữ liệu để trả về
  columns?: IDisplayColumn[],

  // Thuộc tính nhận từ cài đặt modal "Xuất dữ liệu" hỗ trợ user tự đổi tên theo ý
  fileNew?: {
    fileName: string,
    sheetName: string,
  },
}

type ExportFacebookData = {
  // Bản cũ đang chỉ thấy hỗ trợ chính xác loại này
  // Nếu muốn dùng mở rộng thêm loại khác nữa thì cần sửa thêm
  value: 'fbPsid';
  name: string;
  icon: string;
  iconTitle: string;
}

export interface IOptionsExcelSetting {
  businessId: number;
  excelService?: ExcelService;
  /**
   * Danh sách "lựa chọn dữ liệu"
   * + Giá trị truyền vào là text đã translate nếu cần dịch.
   * + Chỉ hỗ trợ 4 loại "recordAll | recordPage | recordGroupPsParent | recordSelected"
   * */
  excelListPage?: {
    checked: listPageName;
    list: {
      key: listPageName,
      value: string,
    }[];
  };
  exportFacebook?: ExportFacebookData

  /**
   * Cho lựa chọn lặp thông tin sản phẩm không, mặc định không có
   * + Các trang thuường dùng như: Bán hàng, đơn hàng...
   * */
  recordAllFullPage?: string;

  title?: string; // modal-title Mặc định "Xuất excel"

  /**
   * Một số trang sẽ hỗ trợ logic "Lưu cài đặt này cho lần sau" thì cần truyền type vào
   * */
  typeExcelSetting?: number; // Bản cũ là "type"

  /**
   * Cột thì không bắt buộc tuỳ theo nghiệp vụ từng màn, cơ bản sẽ có 3 dạng
   * - Không có columns[]: /pos/bill/detail
   * - Có columns[] nhưng không lưu cài đặt: ...
   * - Có columns[] có lưu cài đặt: /product/item/index
   * */
  columns?: IDisplayColumn[]
}

@Component({
  standalone: false,
  selector: 'app-export-excel-setting',
  templateUrl: 'export-excel-setting.html',
  styles: [`
 .scrollbar-columns {
 max-height: 300px;
 overflow-y: auto;
 margin-right: -0.25rem;
 padding: 1rem;
 }

 .excel-loading-icon {
 display: inline-block;
 animation: excel-loading-spin 1s linear infinite;
 }

 @keyframes excel-loading-spin {
 to {
 transform: rotate(360deg);
 }
 }
 `]
})
export class ExportExcelSettingModalComponent implements OnInit, OnDestroy {
  @Input() options!: IOptionsExcelSetting
  @Output() output = new EventEmitter<IExcelSettingOutput>();
  private readonly destroyRef = inject(DestroyRef);

  states: IStates = {
    columnExcelOption: 'all',
    colsAll: false,
    recordAllFullPage: false,
    excelPageChecked: 'recordAll',
    settingColumns: [],
    isSubmit: false,
    rembExcelSetting: true,
  }


  progresState: { currentItem: number, totalItems: number, totalPages: number, currentPage: number } = {
    currentItem: 0,
    totalItems: 0,
    totalPages: 0,
    currentPage: 0,
  }


  constructor(
    public activeModal: NgbActiveModal,
    // public commonIDBService: CommonIDBService,
    public appService: AppService,
    public userService: UserService,
    public authService: AuthService,
    private percent: FormatPercentPipe
  ) {
  }

  ngOnInit() {
    const {columns, typeExcelSetting, excelListPage, recordAllFullPage, excelService} = this.options;

    if (columns) {
      this.states.settingColumns = [...columns]
    }
    if (excelListPage) {
      this.states.excelPageChecked = excelListPage.checked
    }
    if (typeExcelSetting) {
      this.loadSettingColumnsExport(typeExcelSetting);
    }


    if (excelService) {
      excelService.progressPaginator.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: res => {
          if (res == null) {
            // Handler finnal
            return;
          }
          const cal = res.page * res.pageSize;
          this.progresState.totalItems = res.totalItems;
          this.progresState.currentItem = cal > res.totalItems ? res.totalItems : cal;
          this.progresState.totalPages = res.totalPages;
          this.progresState.currentPage = res.page;

          if (this.progresState.currentPage > 0 || this.progresState.totalPages > 0) {
            const calculator = this.percent.transform(
              (this.progresState.currentPage / this.progresState.totalPages) * 100
            ) as number;

            if (calculator >= 100) {
              setTimeout(() => {
                this.activeModal.dismiss(true)
              }, 200)
            }
          }
        }
      })
    }
  }

  ngOnDestroy() {
  }


  /**
   * Events -------------------------------------------------------------------------------------------------------
   * */
  colChange(value: any, col: ISettingColumnsExcel) {
    col.display = value === 1;
    this._calIsCheckedAll()
  }

  onCheckAll() {
    this.states.colsAll = !this.states.colsAll;
    this.states.settingColumns.forEach(item => {
      item.display = this.states.colsAll;
    });
  }

  private _calIsCheckedAll() {
    this.states.colsAll = this.states.settingColumns.every(f => {
      if (f.childs) {
        // Kiểm tra tất cả phần tử con
        return f.childs.every(fc => fc.display || fc.required);
      } else {
        // Kiểm tra phần tử cha
        return f.display || f.required;
      }
    });
  }

  /**
   * - Chỉ với loại "tự chọn cột" thì columns trả về mới chỉ định thêm display = false
   * - Các loại còn lại columns sẽ được set toàn bộ display = true
   *
   * @Note:
   * - Đối với
   * */
  onExportData() {
    const {typeExcelSetting, exportFacebook: fbData} = this.options;

    const states = this.states;
    states.isSubmit = true;

    const output: IExcelSettingOutput = {
      params: {
        format: "jsExcel",
        exportGroupToPsParent: states.excelPageChecked == 'recordGroupPsParent',
        typeExcel: states.recordAllFullPage ? 'full' : '',
      },
      // KQ lấy từ dòng "Chọn dữ liệu"
      exportType: 'all',
    }

    switch (states.excelPageChecked) {
      case "recordAll":
        output.exportType = 'all';
        break;

      case "recordSelected":
        output.exportType = 'records';
        break;

      case "recordPage":
        output.exportType = 'current';
        break;
    }

    // Loại extra này đang hỗ trợ xuất dữ liêu theo fbPsId
    if (fbData && states.columnExcelOption == fbData.value) {
      output.params.typeExcel = fbData.value;
    }

    if (states.settingColumns.length) {
      output.columns = states.settingColumns;
    }

    if (typeExcelSetting && states.rembExcelSetting) {
      this.handleSave(typeExcelSetting, output);
    }

    if (states.columnExcelOption == 'all' && output.columns) {
      output.columns.forEach(c => c.display = true);
    }
    this.output.next(output);
  }


  /**
   * Handle API -------------------------------------------------------------------------------------------------------
   * */

  handleSave(type: number, output: IExcelSettingOutput) {
    if (!type || type == 0) return;

    const {columns, params} = output;

    if (columns && this.states.rembExcelSetting && params.typeExcel != 'fbPsid') {
      const contents: { excelListPage: string, column?: string[], } = {
        excelListPage: this.states.excelPageChecked
      }
      if (columns && this.states.columnExcelOption != 'all') {
        contents.column = [];
        columns.forEach(c => {
          if (c.display) {
            contents.column?.push(c.ref)
          }
        });
      }

      // 2. Save config for IndexedDB.
      const obj: IColumnsDisplayLocal = {
        userId: this.appService.getUserId,
        type: type,
        columns: columns.map(col => {
          return {
            show: col.display ? 1 : 0,
            colId: col.ref
          }
        })
      }
      // this.commonIDBService.saveUserSetting(obj) vpage chưa dùng tới comment lại
      //
      // // 3. Save config for Database POS.
      // this.userService.postRequest('/setting/store/exportexcel', {
      // content: JSON.stringify(contents),
      // type: type,
      // businessId: this.authService.businessCurrent?.id
      // }).subscribe()
    }

  }

  loadSettingColumnsExport(type: number) {
    if (!type) {
      return;
    }
    //vpage chưa dùng tới comment lại
    // this.userService.postRequest<IResponseApi<IResSetting>>('/setting/store/exportexcel', {
    // type: type,
    // businessId: this.authService.businessCurrent?.id,
    // loadSetting: 1
    // }).subscribe({
    // next: res => {
    // if (!res.code) {
    // return;
    // }
    //
    // const {column, excelListPage} = res.data;
    //
    // this.states.excelPageChecked = excelListPage;
    //
    // if (isArray(column) && column?.length) {
    // this.states.columnExcelOption = 'column';
    //
    // if (this.states.settingColumns.length) {
    // this.states.settingColumns.forEach(cl => {
    // cl.display = column.includes(cl.ref)
    // })
    // setTimeout(() => this._calIsCheckedAll())
    // }
    // } else {
    // this.states.columnExcelOption = 'all';
    // }
    // }
    // })
  }
}
